import { Download } from 'lucide-react';
import { FilterSelect } from '../components/FilterSelect';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { NormalizedDetailRow } from '../types';
import { colorNeed, colorQc, colorStatus, exportCsv, num, pct, sortNewestFirst, uniq } from '../utils';

interface DetailFilters {
  q: string;
  store: string;
  staff: string;
  status: string;
  need: string;
  industry: string;
  brand: string;
  month: string;
  contact: string;
  qc: string;
}

interface DetailScreenProps {
  rows: NormalizedDetailRow[];
  loaded: boolean;
  busy: boolean;
  filters: DetailFilters;
  setFilters: (filters: DetailFilters) => void;
  storeList: string[];
}

const emptyFilters: DetailFilters = { q: '', store: '', staff: '', status: '', need: '', industry: '', brand: '', month: '', contact: '', qc: '' };

export function DetailScreen({ rows, loaded, busy, filters, setFilters, storeList }: DetailScreenProps) {
  if (busy && !loaded) return <div className="card empty">Đang tải danh sách tư vấn...</div>;
  if (!loaded) return <div className="card empty">Mở Danh sách tư vấn để tải dữ liệu.</div>;
  const options = {
    stores: storeList.length ? storeList : uniq(rows.map(row => row.store_code)),
    staff: uniq(rows.map(row => row.staff_name)),
    statuses: uniq(rows.map(row => row.store_care_status_label)),
    need: uniq(rows.map(row => row.need_type_label)),
    industries: uniq(rows.map(row => row.industry)),
    brands: uniq(rows.map(row => row.brand).filter(value => value && value !== 'Chưa rõ')),
    months: uniq(rows.map(row => row.month).filter(Boolean)),
  };
  const filtered = sortNewestFirst(rows.filter(row => {
    const haystack = `${row.customer} ${row.phone_masked || ''} ${row.need_text} ${row.staff_name} ${row.industry} ${row.brand} ${row.product}`.toLowerCase();
    return (!filters.q || haystack.includes(filters.q.toLowerCase()))
      && (!filters.store || row.store_code === filters.store)
      && (!filters.staff || row.staff_name === filters.staff)
      && (!filters.status || row.store_care_status_label === filters.status)
      && (!filters.need || row.need_type_label === filters.need)
      && (!filters.industry || row.industry === filters.industry)
      && (!filters.brand || row.brand === filters.brand)
      && (!filters.month || row.month === filters.month)
      && (!filters.contact || (filters.contact === 'contactable' ? row.has_contact : !row.has_contact))
      && (!filters.qc || row.agent_qc_status === filters.qc);
  }));
  const total = filtered.length;
  const contact = filtered.filter(row => row.has_contact).length;
  const followups = filtered.filter(row => row.has_contact && ['hopeful', 'not_closed', 'service', 'new'].includes(row.store_care_status)).slice(0, 120);
  const patch = (update: Partial<DetailFilters>) => setFilters({ ...filters, ...update });

  return (
    <>
      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Store</span>
          <button className={`tab ${!filters.store ? 'active' : ''}`} onClick={() => patch({ store: '' })} type="button">Tất cả</button>
          {options.stores.map(store => <button key={store} className={`tab ${filters.store === store ? 'active' : ''}`} onClick={() => patch({ store })} type="button">{store}</button>)}
        </div>
        <div className="filter-grid">
          <div className="control"><label htmlFor="detail-search">Tìm nhanh</label><input id="detail-search" value={filters.q} onChange={event => patch({ q: event.target.value })} placeholder="Tên, SĐT, nhu cầu, sản phẩm..." /></div>
          <FilterSelect label="Nhân viên" value={filters.staff} onChange={staff => patch({ staff })} options={options.staff} />
          <FilterSelect label="Trạng thái" value={filters.status} onChange={status => patch({ status })} options={options.statuses} />
          <FilterSelect label="Nhu cầu" value={filters.need} onChange={need => patch({ need })} options={options.need} />
          <FilterSelect label="Ngành hàng" value={filters.industry} onChange={industry => patch({ industry })} options={options.industries} />
          <FilterSelect label="Thương hiệu" value={filters.brand} onChange={brand => patch({ brand })} options={options.brands} />
          <FilterSelect label="Tháng" value={filters.month} onChange={month => patch({ month })} options={options.months} />
        </div>
        <div className="actions start">
          <FilterSelect label="Liên hệ" value={filters.contact} onChange={contactValue => patch({ contact: contactValue })} options={['contactable', 'missing_contact']} />
          <FilterSelect label="Chất lượng dữ liệu" value={filters.qc} onChange={qc => patch({ qc })} options={['passed', 'missing_contact', 'needs_review', 'rejected']} />
          <button className="btn" onClick={() => setFilters(emptyFilters)} type="button">Xóa lọc</button>
          <button aria-label="Xuất danh sách tư vấn" className="btn primary" onClick={() => exportCsv(filtered)} type="button"><Download size={15} />Xuất danh sách</button>
        </div>
      </section>

      <section className="grid kpis">
        <KpiCard label="Lượt tư vấn đang xem" value={num(total)} sub="Sau bộ lọc" />
        <KpiCard label="Có liên hệ" value={num(contact)} sub={`${pct(total ? contact * 100 / total : 0)} có thể chăm lại`} tone="green" />
        <KpiCard label="Thiếu liên hệ" value={num(total - contact)} sub="Chưa có SĐT" tone="red" />
        <KpiCard label="Mua hàng" value={num(filtered.filter(row => row.need_type === 'sales').length)} sub="Nhu cầu mua sản phẩm" tone="blue" />
        <KpiCard label="Dịch vụ" value={num(filtered.filter(row => row.need_type === 'service').length)} sub="Bảo hành, vệ sinh" tone="violet" />
        <KpiCard label="Cần kiểm tra" value={num(filtered.filter(row => row.agent_qc_status === 'needs_review').length)} sub="Thiếu thông tin" tone="amber" />
      </section>

      <section className="card space">
        <div className="section-title"><h2>Hàng đợi chăm sóc</h2><span>{num(followups.length)} khách có liên hệ</span></div>
        <RecordTable rows={followups} limit={120} />
      </section>

      <section className="card">
        <div className="section-title"><h2>Chi tiết tư vấn</h2><span>{num(filtered.length)} lượt sau lọc (hiện 500 đầu)</span></div>
        <RecordTable rows={filtered} limit={500} />
      </section>
    </>
  );
}

function RecordTable({ rows, limit }: { rows: NormalizedDetailRow[]; limit: number }) {
  return (
    <div className="scroll">
      <table>
        <thead><tr><th>Ngày</th><th>Nguồn</th><th>Cửa hàng</th><th>Nhân viên</th><th>Khách</th><th>SĐT</th><th>Nhu cầu</th><th>Ngành</th><th>Trạng thái</th><th>Chất lượng</th></tr></thead>
        <tbody>{rows.slice(0, limit).map(row => (
          <tr key={row.capture_id}>
            <td className="tnum muted">{row.day}</td>
            <td>{row.source_label}</td>
            <td><b>{row.store_code}</b></td>
            <td>{row.staff_name}</td>
            <td>{row.customer}</td>
            <td className="tnum">{row.phone_masked || '-'}</td>
            <td className="need"><Tag tone={colorNeed(row.need_type_label)}>{row.need_type_label}</Tag><div className="gap4" />{row.need_text}</td>
            <td>{row.industry}</td>
            <td><Tag tone={colorStatus(row.store_care_status_label)}>{row.store_care_status_label}</Tag></td>
            <td><Tag tone={colorQc(row.agent_qc_status)}>{row.agent_qc_status_label}</Tag></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
